import { useEffect, useRef } from "react";
import {
  CalendarIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { DatePicker } from "components/shared/form/Datepicker";
import { TimePicker } from "components/shared/form/Timepicker";
import { timeSlots } from "constants/timeSlots.constant";
import { getLocalISODate } from "utils/dateUtils";

// Helper to parse date safely (supporting Date objects, YYYY-MM-DD strings, etc.)
const parseScheduledDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;

  // Parse YYYY-MM-DD (e.g. from getLocalISODate)
  const match = String(date).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [_, y, m, d] = match;
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }

  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// Helper to check if a selected date and time is in the past
const isScheduleInPast = (date, time) => {
  const dateObj = parseScheduledDate(date);
  if (!dateObj) return false;

  const now = new Date();
  now.setSeconds(0, 0);
  const [hours, minutes] = time.split(":").map(Number);
  dateObj.setHours(hours, minutes, 0, 0);

  return dateObj < now;
};

/**
 * EmailScheduleToggle Component
 *
 * A reusable toggle + date/time picker for scheduling email or SMS delivery.
 * Used in both EmailMessageModal and ActivityComposer.
 *
 * Props:
 * - enabled: boolean – whether scheduling is toggled on
 * - onToggle: () => void – toggle handler
 * - scheduledDate: string – selected date value (YYYY-MM-DD)
 * - onDateChange: (value: string) => void
 * - scheduledTime: string – selected time value (HH:mm)
 * - onTimeChange: (value: string) => void
 * - label: string – optional label text (defaults to "Schedule for later")
 * - variant: "compact" | "full" – styling variant ("compact" for modal, "full" for form layout)
 */
export const EmailScheduleToggle = ({
  enabled,
  onToggle,
  scheduledDate,
  onDateChange,
  scheduledTime,
  onTimeChange,
  label = "Schedule for later",
  variant = "compact",
  disabled = false,
  isEditMode = false,
}) => {
  const lastDateRef = useRef(scheduledDate);
  const lastEnabledRef = useRef(enabled);

  // Auto-adjust time if it's in the past
  useEffect(() => {
    if (isEditMode) return;

    const dateChanged = lastDateRef.current !== scheduledDate;
    const enabledChanged = lastEnabledRef.current !== enabled;
    lastDateRef.current = scheduledDate;
    lastEnabledRef.current = enabled;

    if (enabled && scheduledDate) {
      if (dateChanged || enabledChanged || !scheduledTime) {
        const currentTime = scheduledTime || "09:00";
        if (isScheduleInPast(scheduledDate, currentTime)) {
          const now = new Date();
          const currentHours = String(now.getHours()).padStart(2, "0");
          const currentMinutes = String(now.getMinutes()).padStart(2, "0");
          onTimeChange(`${currentHours}:${currentMinutes}`);
        } else if (!scheduledTime) {
          onTimeChange("09:00");
        }
      }
    }
  }, [enabled, scheduledDate, scheduledTime, onTimeChange, isEditMode]);

  if (variant === "full") {
    // Full variant: side-by-side date & time without toggle (always visible)
    return (
      <div className="space-y-3">
        {/* Toggle Row */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={disabled ? undefined : onToggle}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${enabled ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"} ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
            role="switch"
            aria-checked={enabled}
            disabled={disabled}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? "translate-x-4" : "translate-x-0"}`}
            />
          </button>

          <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300">
            <CalendarIcon className="h-4 w-4 text-gray-500" />
            {label}
          </span>
          {!enabled && (
            <span className="animate-in fade-in slide-in-from-left-2 flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0 text-[11px] font-medium text-blue-600 duration-300 dark:border-blue-900/30 dark:bg-blue-950/20 dark:text-blue-400">
              <InformationCircleIcon className="h-3.5 w-3.5" />
              It will be sent instantly.
            </span>
          )}
        </div>

        {/* Date & Time Inputs */}
        <div
          className={`grid grid-cols-1 gap-4 rounded-lg border p-3.5 transition-all duration-200 md:grid-cols-2 ${
            enabled
              ? "border-blue-200 bg-white dark:border-blue-800 dark:bg-gray-800"
              : "border-gray-200 bg-gray-50/50 opacity-75 dark:border-gray-700 dark:bg-gray-800/20"
          }`}
        >
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
              Delivery Date <span className="text-red-500">*</span>
            </label>
            <div className="w-full">
              <DatePicker
                value={parseScheduledDate(scheduledDate)}
                onChange={([date]) => onDateChange(getLocalISODate(date))}
                disabled={disabled || !enabled}
                placeholder="Choose delivery date"
                options={{
                  minDate: (isEditMode && scheduledDate && parseScheduledDate(scheduledDate) < new Date(new Date().setHours(0,0,0,0))) 
                  ? parseScheduledDate(scheduledDate) 
                  : "today",
                  dateFormat: "d/m/Y",
                  disableMobile: true,
                }}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
              Delivery Time <span className="text-red-500">*</span>
            </label>
            <TimePicker
              value={scheduledTime || "09:00"}
              onChange={(e) => onTimeChange(e.target.value)}
              disabled={disabled || !enabled}
              className="w-full"
              min={(() => {
                if (scheduledDate) {
                  const today = new Date();
                  const selDate = parseScheduledDate(scheduledDate);
                  if (
                    selDate &&
                    !isNaN(selDate.getTime()) &&
                    selDate.toDateString() === today.toDateString()
                  ) {
                    const currentHours = String(today.getHours()).padStart(
                      2,
                      "0",
                    );
                    const currentMinutes = String(today.getMinutes()).padStart(
                      2,
                      "0",
                    );
                    return `${currentHours}:${currentMinutes}`;
                  }
                }
                return undefined;
              })()}
            />
          </div>
        </div>
      </div>
    );
  }

  // Compact variant (default) – used in EmailMessageModal
  return (
    <div className="space-y-3.5 rounded-xl border border-gray-200 bg-gray-50 p-3.5 dark:border-gray-700 dark:bg-gray-800/40">
      {/* Toggle Row */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={disabled ? undefined : onToggle}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${enabled ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"} ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
          role="switch"
          aria-checked={enabled}
          disabled={disabled}
        >
          <span
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? "translate-x-4" : "translate-x-0"}`}
          />
        </button>

        <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <CalendarIcon className="h-4 w-4 text-gray-500" />
          {label}
        </span>
        {!enabled && (
          <span className="animate-in fade-in slide-in-from-left-2 flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0 text-[11px] font-medium text-blue-600 duration-300 dark:border-blue-900/30 dark:bg-blue-950/20 dark:text-blue-400">
            <InformationCircleIcon className="h-3.5 w-3.5" />
            It will be sent instantly.
          </span>
        )}
      </div>

      <div
        className={`grid grid-cols-2 gap-3.5 rounded-lg border p-3.5 transition-all duration-200 ${
          enabled
            ? "border-blue-200 bg-white dark:border-blue-800 dark:bg-gray-800"
            : "border-gray-200 bg-gray-50/50 opacity-75 dark:border-gray-700 dark:bg-gray-800/20"
        }`}
      >
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
            Delivery Date
          </label>
          <div className="w-full">
            <DatePicker
              value={parseScheduledDate(scheduledDate)}
              onChange={([date]) => onDateChange(getLocalISODate(date))}
              disabled={disabled || !enabled}
              placeholder="Choose delivery date"
              options={{
                minDate: (isEditMode && scheduledDate && parseScheduledDate(scheduledDate) < new Date(new Date().setHours(0,0,0,0))) 
                  ? parseScheduledDate(scheduledDate) 
                  : "today",
                dateFormat: "d/m/Y",
                disableMobile: true,
              }}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
            Delivery Time
          </label>
          <TimePicker
            value={scheduledTime || "09:00"}
            onChange={(e) => onTimeChange(e.target.value)}
            disabled={disabled || !enabled}
            className="w-full"
            min={(() => {
              if (scheduledDate) {
                const today = new Date();
                const selDate = parseScheduledDate(scheduledDate);
                if (
                  selDate &&
                  !isNaN(selDate.getTime()) &&
                  selDate.toDateString() === today.toDateString()
                ) {
                  const currentHours = String(today.getHours()).padStart(
                    2,
                    "0",
                  );
                  const currentMinutes = String(today.getMinutes()).padStart(
                    2,
                    "0",
                  );
                  return `${currentHours}:${currentMinutes}`;
                }
              }
              return undefined;
            })()}
          />
        </div>
      </div>
    </div>
  );
};

export default EmailScheduleToggle;
